import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  CreditCard, 
  Ticket, 
  ClipboardList,
  LogOut,
  ChevronRight,
  Database,
  Activity,
  ShieldCheck,
  TrendingUp,
  Target,
  Calendar,
  DollarSign,
  UserPlus,
  Filter,
  X,
  GitPullRequestArrow
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import RoleGuard from '../../guards/RoleGuard';

const showSuperAdminSetup = import.meta.env.DEV || import.meta.env.VITE_ENABLE_SUPERADMIN_SETUP === 'true';

const Sidebar = ({ open = false, onClose = () => {}, onNavigate = () => {} }) => {
  const { logout, user } = useAuth();

  const links = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/tenants', label: 'Workspaces', icon: Building2 },
    { to: '/admin-users', label: 'Admin Users', icon: Users, role: 'SuperAdmin' },
    { to: '/subscription-plans', label: 'Subscription Plans', icon: CreditCard },
    { to: '/tickets', label: 'Tickets', icon: Ticket },
    { to: '/signup-requests', label: 'Signups', icon: UserPlus },
    { to: '/change-requests', label: 'Change Requests', icon: GitPullRequestArrow },
    { to: '/audit-logs', label: 'Audit Logs', icon: ClipboardList },
    { to: '/provisioning', label: 'Setup Queue', icon: Activity },
  ];

  const growthLinks = [
    { to: '/growth', label: 'Growth', icon: TrendingUp },
    { to: '/growth/prospects', label: 'Prospects', icon: Users },
    { to: '/growth/pipeline', label: 'Pipeline', icon: Target },
    { to: '/growth/tasks', label: 'Tasks', icon: ClipboardList },
    { to: '/growth/pilots', label: 'Pilots', icon: Calendar },
    { to: '/growth/invoices', label: 'Invoices', icon: DollarSign },
    { to: '/growth/customers', label: 'Customers', icon: Building2 },
    { to: '/growth/funnels', label: 'Funnels', icon: Filter },
  ];

  return (
    <>
      {/* Mobile backdrop — tap to dismiss the drawer. Hidden on lg+ where the sidebar is static. */}
      {open && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-white border-r border-slate-100 flex flex-col transform transition-transform duration-300 ease-in-out lg:static lg:z-20 lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full lg:hidden'}`}
      >
      <div className="p-8 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-200">
            <Database className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-black tracking-tight text-slate-900">BIZCORE<span className="text-primary-600">.</span></span>
        </div>
        <button
          onClick={onClose}
          aria-label="Close menu"
          className="lg:hidden p-2 -mr-2 text-slate-400 hover:text-slate-900 rounded-xl transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Collapse the drawer after a link is tapped on mobile only; keep it open on desktop navigation. */}
      <nav onClick={onNavigate} className="flex-1 px-4 py-4 space-y-2 overflow-y-auto no-scrollbar">
        {/* Main Navigation */}
        {links.filter(l => !l.role || l.role === user?.role).map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className={({ isActive }) => `
              flex items-center justify-between px-4 py-3.5 rounded-2xl transition-all duration-300 group
              ${isActive 
                ? 'bg-slate-900 text-white shadow-xl shadow-slate-200 translate-x-1' 
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}
            `}
          >
            <div className="flex items-center space-x-3">
              <link.icon className={`w-5 h-5 transition-transform group-hover:scale-110`} />
              <span className="font-bold text-sm tracking-wide">{link.label}</span>
            </div>
            <ChevronRight className={`w-4 h-4 opacity-0 transition-opacity duration-300 group-hover:opacity-50`} />
          </NavLink>
        ))}

        {/* Growth Navigation */}
        <div className="pt-4 mt-4 border-t border-slate-200">
          <p className="px-4 mb-2 text-xs font-bold text-slate-400 uppercase tracking-wider">Growth</p>
          {growthLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => `
                flex items-center justify-between px-4 py-3 rounded-2xl transition-all duration-300 group
                ${isActive 
                  ? 'bg-primary-600 text-white shadow-xl shadow-primary-200 translate-x-1' 
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}
              `}
            >
              <div className="flex items-center space-x-3">
                <link.icon className={`w-5 h-5 transition-transform group-hover:scale-110`} />
                <span className="font-bold text-sm tracking-wide">{link.label}</span>
              </div>
              <ChevronRight className={`w-4 h-4 opacity-0 transition-opacity duration-300 group-hover:opacity-50`} />
            </NavLink>
          ))}
        </div>

        {showSuperAdminSetup && (
          <RoleGuard allowedRoles={['SuperAdmin']}>
            <NavLink
              to="/seed-superadmin"
              className={({ isActive }) => `
                flex items-center justify-between px-4 py-3.5 rounded-2xl transition-all duration-300 group
                ${isActive 
                  ? 'bg-slate-900 text-white shadow-xl shadow-slate-200 translate-x-1' 
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}
              `}
            >
              <div className="flex items-center space-x-3">
                <ShieldCheck className="w-5 h-5 transition-transform group-hover:scale-110" />
                <span className="font-bold text-sm tracking-wide">Setup</span>
              </div>
              <ChevronRight className="w-4 h-4 opacity-0 transition-opacity duration-300 group-hover:opacity-50" />
            </NavLink>
          </RoleGuard>
        )}
      </nav>

      <div className="p-6">
        <button 
          onClick={logout}
          className="w-full flex items-center space-x-3 px-4 py-4 text-rose-500 hover:bg-rose-50 rounded-2xl transition-all font-bold group"
        >
          <LogOut className="w-5 h-5 group-hover:rotate-12 transition-transform" />
          <span>Sign Out</span>
        </button>
      </div>
      </aside>
    </>
  );
};

export default Sidebar;
